import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from './api';
import './Classrooms.css';

// Ступени обучения — 18 строк подряд читать тяжело, по ступеням нужное
// находится заметно быстрее.
const LEVELS = [
  { id: 'primary', title: 'Начальная школа', note: '1 – 4 классы', from: 1, to: 4 },
  { id: 'basic',   title: 'Основная школа',  note: '5 – 9 классы', from: 5, to: 9 },
  { id: 'senior',  title: 'Средняя школа',   note: '10 – 11 классы', from: 10, to: 11 },
];

const gradeNumber = (grade) => {
  const match = /^\s*(\d+)/.exec(grade || '');
  return match ? parseInt(match[1], 10) : NaN;
};

// Тот же порядок, что и на сервере: 1А, 1Б, 2А, ... 10А, 11А.
// Сортируем и на клиенте, чтобы добавленный или переименованный класс
// сразу вставал на своё место, без перезагрузки страницы.
const gradeSortKey = (row) => {
  const match = /^\s*(\d+)\s*(.*)$/.exec(row.grade || '');
  return match
    ? [0, parseInt(match[1], 10), match[2].toLowerCase()]
    : [1, 0, (row.grade || '').toLowerCase()];
};

const byGrade = (a, b) => {
  const ka = gradeSortKey(a);
  const kb = gradeSortKey(b);
  for (let i = 0; i < ka.length; i += 1) {
    if (ka[i] < kb[i]) return -1;
    if (ka[i] > kb[i]) return 1;
  }
  return 0;
};

const EMPTY_DRAFT = { grade: '', room: '', teacher: '', tutor: '' };

// Ошибки валидации приходят как {"grade": ["Такой класс уже есть"]} —
// показываем пользователю текст, а не JSON.
const readError = (err) => {
  const message = (err && err.message) || 'Не удалось сохранить изменения';
  if (message === 'SESSION_EXPIRED') return message;
  try {
    const parsed = JSON.parse(message);
    const first = Object.values(parsed)[0];
    return Array.isArray(first) ? first[0] : String(first);
  } catch (e) {
    return message;
  }
};

export default function Classrooms({ user }) {
  const isAdmin = !!(user && (user.role === 'admin' || user.is_admin || user.is_superuser));

  const [rows, setRows] = useState([]);
  const [schoolYear, setSchoolYear] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [query, setQuery] = useState('');
  const [actionError, setActionError] = useState(null);
  const [saving, setSaving] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(EMPTY_DRAFT);

  const [creating, setCreating] = useState(false);
  const [createDraft, setCreateDraft] = useState(EMPTY_DRAFT);

  const [editingYear, setEditingYear] = useState(false);
  const [yearDraft, setYearDraft] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const [list, settings] = await Promise.all([
        api.getClassrooms(),
        api.getClassroomSettings(),
      ]);
      setRows(Array.isArray(list) ? list : []);
      setSchoolYear((settings && settings.school_year) || '');
    } catch (err) {
      setLoadError(readError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const source = q
      ? rows.filter((row) =>
          [row.grade, row.room, row.teacher, row.tutor]
            .filter(Boolean)
            .some((value) => value.toLowerCase().includes(q))
        )
      : rows;
    return [...source].sort(byGrade);
  }, [rows, query]);

  const levels = useMemo(() => {
    const used = new Set();
    const grouped = LEVELS.map((level) => {
      const levelRows = filtered.filter((row) => {
        const n = gradeNumber(row.grade);
        const match = n >= level.from && n <= level.to;
        if (match) used.add(row.id);
        return match;
      });
      return { ...level, rows: levelRows };
    });
    // Классы с непривычным названием не должны потеряться
    const rest = filtered.filter((row) => !used.has(row.id));
    if (rest.length > 0) {
      grouped.push({ id: 'other', title: 'Другие', note: '', rows: rest });
    }
    return grouped.filter((level) => level.rows.length > 0);
  }, [filtered]);

  const startEdit = (row) => {
    setActionError(null);
    setCreating(false);
    setEditingId(row.id);
    setDraft({
      grade: row.grade || '',
      room: row.room || '',
      teacher: row.teacher || '',
      tutor: row.tutor || '',
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft(EMPTY_DRAFT);
    setActionError(null);
  };

  const saveEdit = async () => {
    if (!draft.grade.trim()) {
      setActionError('Укажите класс.');
      return;
    }
    try {
      setSaving(true);
      setActionError(null);
      const updated = await api.updateClassroom(editingId, {
        grade: draft.grade.trim(),
        room: draft.room.trim(),
        teacher: draft.teacher.trim(),
        tutor: draft.tutor.trim(),
      });
      setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
      cancelEdit();
    } catch (err) {
      setActionError(readError(err));
    } finally {
      setSaving(false);
    }
  };

  const startCreate = () => {
    setActionError(null);
    setEditingId(null);
    setCreateDraft(EMPTY_DRAFT);
    setCreating(true);
  };

  const saveCreate = async () => {
    if (!createDraft.grade.trim()) {
      setActionError('Укажите класс.');
      return;
    }
    try {
      setSaving(true);
      setActionError(null);
      const created = await api.createClassroom({
        grade: createDraft.grade.trim(),
        room: createDraft.room.trim(),
        teacher: createDraft.teacher.trim(),
        tutor: createDraft.tutor.trim(),
      });
      setRows((prev) => [...prev, created]);
      setCreating(false);
      setCreateDraft(EMPTY_DRAFT);
    } catch (err) {
      setActionError(readError(err));
    } finally {
      setSaving(false);
    }
  };

  const removeRow = async (row) => {
    if (!window.confirm(`Удалить класс ${row.grade} из таблицы кабинетов?`)) return;
    try {
      setSaving(true);
      setActionError(null);
      await api.deleteClassroom(row.id);
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      if (editingId === row.id) cancelEdit();
    } catch (err) {
      setActionError(readError(err));
    } finally {
      setSaving(false);
    }
  };

  const saveYear = async () => {
    try {
      setSaving(true);
      setActionError(null);
      const updated = await api.updateClassroomSettings({ school_year: yearDraft.trim() });
      setSchoolYear(updated.school_year || '');
      setEditingYear(false);
    } catch (err) {
      setActionError(readError(err));
    } finally {
      setSaving(false);
    }
  };

  const onDraftKeyDown = (e, onSave, onCancel) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    }
  };

  if (loading) {
    return (
      <div className="classrooms">
        <div className="classrooms-state">
          <div className="classrooms-spinner"></div>
          <p>Загрузка таблицы кабинетов...</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="classrooms">
        <div className="classrooms-state">
          <h3>Не удалось загрузить таблицу</h3>
          <p>{loadError}</p>
          <button type="button" className="classrooms-button" onClick={load}>
            Повторить
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="classrooms">
      <header className="classrooms-header">
        <div className="classrooms-heading">
          <h1 className="classrooms-title">Распределение кабинетов по классам</h1>

          {editingYear ? (
            <div className="classrooms-year-edit">
              <input
                type="text"
                className="classrooms-input"
                value={yearDraft}
                autoFocus
                onChange={(e) => setYearDraft(e.target.value)}
                onKeyDown={(e) => onDraftKeyDown(e, saveYear, () => setEditingYear(false))}
                aria-label="Учебный год"
              />
              <button type="button" className="classrooms-icon-button save" onClick={saveYear} disabled={saving} title="Сохранить">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              </button>
              <button type="button" className="classrooms-icon-button" onClick={() => setEditingYear(false)} disabled={saving} title="Отмена">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
          ) : (
            <div className="classrooms-year-row">
              <span className="classrooms-year">{schoolYear || 'Учебный год не указан'}</span>
              {isAdmin && (
                <button
                  type="button"
                  className="classrooms-icon-button"
                  onClick={() => { setYearDraft(schoolYear); setEditingYear(true); }}
                  title="Изменить учебный год"
                  aria-label="Изменить учебный год"
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                    <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z"></path>
                  </svg>
                </button>
              )}
            </div>
          )}
        </div>

        <div className="classrooms-tools">
          <div className="classrooms-search">
            <svg className="classrooms-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.35-4.35"></path>
            </svg>
            <input
              type="text"
              className="classrooms-search-input"
              placeholder="Класс, кабинет или фамилия..."
              value={query}
              onChange={(e) => { setQuery(e.target.value); cancelEdit(); }}
              aria-label="Поиск по таблице кабинетов"
            />
            {query && (
              <button
                type="button"
                className="classrooms-search-clear"
                onClick={() => setQuery('')}
                aria-label="Очистить поиск"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            )}
          </div>

          {isAdmin && (
            <button type="button" className="classrooms-add-button" onClick={startCreate}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              Добавить класс
            </button>
          )}
        </div>
      </header>

      {actionError && <div className="classrooms-error">{actionError}</div>}

      {isAdmin && creating && (
        <div className="classrooms-create">
          <div className="classrooms-create-header">
            <h3>Новый класс</h3>
            <button
              type="button"
              className="classrooms-icon-button"
              onClick={() => { setCreating(false); setActionError(null); }}
              aria-label="Закрыть"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>

          <div className="classrooms-create-grid">
            <label className="classrooms-field">
              <span>Класс *</span>
              <input
                type="text"
                className="classrooms-input"
                placeholder="7В"
                value={createDraft.grade}
                autoFocus
                onChange={(e) => setCreateDraft({ ...createDraft, grade: e.target.value })}
                onKeyDown={(e) => onDraftKeyDown(e, saveCreate, () => setCreating(false))}
              />
            </label>
            <label className="classrooms-field">
              <span>Кабинет</span>
              <input
                type="text"
                className="classrooms-input"
                placeholder="2.36"
                value={createDraft.room}
                onChange={(e) => setCreateDraft({ ...createDraft, room: e.target.value })}
                onKeyDown={(e) => onDraftKeyDown(e, saveCreate, () => setCreating(false))}
              />
            </label>
            <label className="classrooms-field">
              <span>Классный руководитель</span>
              <input
                type="text"
                className="classrooms-input"
                value={createDraft.teacher}
                onChange={(e) => setCreateDraft({ ...createDraft, teacher: e.target.value })}
                onKeyDown={(e) => onDraftKeyDown(e, saveCreate, () => setCreating(false))}
              />
            </label>
            <label className="classrooms-field">
              <span>Воспитатель</span>
              <input
                type="text"
                className="classrooms-input"
                value={createDraft.tutor}
                onChange={(e) => setCreateDraft({ ...createDraft, tutor: e.target.value })}
                onKeyDown={(e) => onDraftKeyDown(e, saveCreate, () => setCreating(false))}
              />
            </label>
          </div>

          <div className="classrooms-create-actions">
            <button type="button" className="classrooms-button ghost" onClick={() => setCreating(false)} disabled={saving}>
              Отмена
            </button>
            <button type="button" className="classrooms-button" onClick={saveCreate} disabled={saving}>
              {saving ? 'Сохранение...' : 'Добавить'}
            </button>
          </div>
        </div>
      )}

      {levels.length === 0 ? (
        <div className="classrooms-empty">
          <div className="classrooms-empty-icon">🔍</div>
          <h3>{rows.length === 0 ? 'Таблица пока пуста' : 'Ничего не найдено'}</h3>
          <p>
            {rows.length === 0
              ? (isAdmin ? 'Нажмите «Добавить класс», чтобы заполнить таблицу.' : 'Данные ещё не заполнены администратором.')
              : 'Попробуйте изменить запрос — например, «7Б», «2.33» или фамилию.'}
          </p>
        </div>
      ) : (
        levels.map((level) => (
          <section className="classrooms-level" key={level.id}>
            <div className="classrooms-level-header">
              <h2 className="classrooms-level-title">{level.title}</h2>
              {level.note && <span className="classrooms-level-note">{level.note}</span>}
            </div>

            <div className="classrooms-table-wrapper">
              <table className={`classrooms-table ${isAdmin ? 'editable' : ''}`}>
                <thead>
                  <tr>
                    <th scope="col" className="col-grade">Класс</th>
                    <th scope="col" className="col-room">Кабинет</th>
                    <th scope="col">Классный руководитель</th>
                    <th scope="col">Воспитатель</th>
                    {isAdmin && <th scope="col" className="col-actions">Действия</th>}
                  </tr>
                </thead>
                <tbody>
                  {level.rows.map((row) => (
                    editingId === row.id ? (
                      <tr key={row.id} className="classrooms-row-editing">
                        <td className="col-grade" data-label="Класс">
                          <input
                            type="text"
                            className="classrooms-input compact"
                            value={draft.grade}
                            autoFocus
                            onChange={(e) => setDraft({ ...draft, grade: e.target.value })}
                            onKeyDown={(e) => onDraftKeyDown(e, saveEdit, cancelEdit)}
                            aria-label="Класс"
                          />
                        </td>
                        <td className="col-room" data-label="Кабинет">
                          <input
                            type="text"
                            className="classrooms-input compact"
                            value={draft.room}
                            onChange={(e) => setDraft({ ...draft, room: e.target.value })}
                            onKeyDown={(e) => onDraftKeyDown(e, saveEdit, cancelEdit)}
                            aria-label="Кабинет"
                          />
                        </td>
                        <td data-label="Классный руководитель">
                          <input
                            type="text"
                            className="classrooms-input"
                            value={draft.teacher}
                            onChange={(e) => setDraft({ ...draft, teacher: e.target.value })}
                            onKeyDown={(e) => onDraftKeyDown(e, saveEdit, cancelEdit)}
                            aria-label="Классный руководитель"
                          />
                        </td>
                        <td data-label="Воспитатель">
                          <input
                            type="text"
                            className="classrooms-input"
                            value={draft.tutor}
                            onChange={(e) => setDraft({ ...draft, tutor: e.target.value })}
                            onKeyDown={(e) => onDraftKeyDown(e, saveEdit, cancelEdit)}
                            aria-label="Воспитатель"
                          />
                        </td>
                        <td className="col-actions" data-label="Действия">
                          <div className="classrooms-actions">
                            <button type="button" className="classrooms-icon-button save" onClick={saveEdit} disabled={saving} title="Сохранить">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="20 6 9 17 4 12"></polyline>
                              </svg>
                            </button>
                            <button type="button" className="classrooms-icon-button" onClick={cancelEdit} disabled={saving} title="Отмена">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      <tr key={row.id}>
                        <td className="col-grade" data-label="Класс">
                          <span className="classrooms-grade">{row.grade}</span>
                        </td>
                        <td className="col-room" data-label="Кабинет">
                          {row.room
                            ? <span className="classrooms-room">{row.room}</span>
                            : <span className="classrooms-dash">—</span>}
                        </td>
                        <td data-label="Классный руководитель">
                          {row.teacher
                            ? <span className="classrooms-person">{row.teacher}</span>
                            : <span className="classrooms-dash">—</span>}
                        </td>
                        <td data-label="Воспитатель">
                          {row.tutor
                            ? <span className="classrooms-person classrooms-person-tutor">{row.tutor}</span>
                            : <span className="classrooms-dash">—</span>}
                        </td>
                        {isAdmin && (
                          <td className="col-actions" data-label="Действия">
                            <div className="classrooms-actions">
                              <button type="button" className="classrooms-icon-button" onClick={() => startEdit(row)} title="Редактировать" aria-label={`Редактировать ${row.grade}`}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                  <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z"></path>
                                </svg>
                              </button>
                              <button type="button" className="classrooms-icon-button danger" onClick={() => removeRow(row)} disabled={saving} title="Удалить" aria-label={`Удалить ${row.grade}`}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <polyline points="3 6 5 6 21 6"></polyline>
                                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                </svg>
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    )
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ))
      )}
    </div>
  );
}
